import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { client, SiteSummaryDto } from "@/client";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useTimeSettings } from "@/state/time-settings";
import { usePermissions } from "@/hooks/use-permissions";
import useBreakpoint from "@/hooks/use-breakpoint";
import { useTooltipProps } from "@/components/custom/tooltip";
import { FloatingMenu } from "@/components/custom/floating-menu";
import { selectColumn } from "@/components/custom/data-table/data-table-select-column";
import { useTableDeleteRefresh } from "@/components/custom/data-table/data-table-context";
import { PiClock, PiBuildings } from "react-icons/pi";
import { UploadCatalogDialog } from "@/routes/dashboard/data/catalog/-upload-catalog-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import { showActionErrorToast } from "@/state/error-toast";

function SiteFloatingMenu({ site }: { site: SiteSummaryDto }) {
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  const deleteRefresh = useTableDeleteRefresh();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const id = site.id;

  const sitesClient = useMutation({
    ...client.sites.update.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
  });

  const menuRows = [
    permissions.catalog.edit
      ? {
          text: t("uploadCatalog"),
          onClick: () => setUploadDialogOpen(true),
        }
      : null,
    permissions.sites.delete
      ? {
          text: "Remove",
          onClick: async () => {
            await sitesClient.mutateAsync({
              id,
              requestBody: {
                isVisible: false,
              },
            });
            await deleteRefresh(id);
          },
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <>
      <FloatingMenu menuRows={menuRows} />
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl">
          <VisuallyHidden>
            <DialogTitle>{t("uploadCatalog")}</DialogTitle>
            <DialogDescription>
              {t("Upload your product catalog CSV file")}
            </DialogDescription>
          </VisuallyHidden>
          <UploadCatalogDialog
            onClose={() => setUploadDialogOpen(false)}
            initialSiteId={id}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export const useColumns = (): ColumnDef<SiteSummaryDto, any>[] => {
  const { t } = useTranslation();
  const timeSettings = useTimeSettings();
  const { permissions } = usePermissions();
  const isMobile = !useBreakpoint("md");
  const tooltipProps = useTooltipProps();

  return useMemo(() => {
    const columns: (ColumnDef<SiteSummaryDto, any> | boolean)[] = [
      permissions.sites.edit && (selectColumn as any),
      {
        header: t("name"),
        accessorFn: (row) => row.name,
        size: isMobile ? 200 : 50,
        meta: {
          // width: isMobile ? "2fr" : "0.5fr",
        },
        cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <div {...tooltipProps} data-tooltip-content={cell.getValue()}>
              {value}
            </div>
          );
        },
      },
      !isMobile && {
        header: t("ready"),
        accessorFn: (row) => row.readyDevices.length,
        size: isMobile ? 100 : 50,
        meta: {
          // width: isMobile ? "1fr" : "0.5fr",
        },
        cell: ({ cell }) => {
          const value = cell.getValue();
          return <span>{value}</span>;
        },
      },
      !isMobile && {
        header: t("offline"),
        accessorFn: (row) => row.offlineDevices.length,
        size: isMobile ? 100 : 50,
        meta: {
          // width: isMobile ? "1fr" : "0.5fr",
        },
        cell: ({ cell }) => {
          const value = cell.getValue();
          return <span>{value}</span>;
        },
      },
      !isMobile && {
        header: t("total"),
        accessorFn: (row) =>
          row.offlineDevices.length + row.readyDevices.length,
        size: isMobile ? 100 : 50,
        meta: {
          // width: isMobile ? "1fr" : "0.5fr",
        },
        cell: ({ cell }) => {
          const value = cell.getValue();
          return <span>{value}</span>;
        },
      },
      {
        header: t("agentVersion"),
        accessorKey: "agentVersion",
        cell: ({ cell }) => {
          const value = cell.getValue();
          return <span>{value}</span>;
        },
      },
      {
        header: t("activationTime"),
        accessorKey: "activationTime",
        cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return <></>;
          const content = timeSettings.renderTime(value);
          return <span>{content}</span>;
        },
      },
      !isMobile && {
        header: "",
        accessorKey: "hasOperationalHours",
        size: 50,
        cell: ({ cell }) => {
          const hasOperationalHours = cell.getValue() as boolean;
          const siteGroup = cell.row.original.siteGroup;

          if (!hasOperationalHours && !siteGroup) return null;

          return (
            <div className="flex items-center gap-2">
              {hasOperationalHours && (
                <div
                  {...tooltipProps}
                  data-tooltip-content={`${t("operationalHours")} ${t("enabled")}`}
                >
                  <PiClock size={18} />
                </div>
              )}
              {siteGroup && (
                <div {...tooltipProps} data-tooltip-content={siteGroup.name}>
                  <PiBuildings size={18} />
                </div>
              )}
            </div>
          );
        },
      },
      !isMobile && {
        id: "floating-menu",
        header: "",
        accessorFn: (row) => row.name,
        meta: {
          disablePreview: true,
          width: "48px",
          minWidth: "48px",
          maxWidth: "48px",
        },
        size: 48,
        minSize: 48,
        maxSize: 48,
        cell: ({ cell }) => <SiteFloatingMenu site={cell.row.original} />,
      },
    ];
    return columns.filter(Boolean) as any;
  }, [t, timeSettings, isMobile, tooltipProps]);
};
